import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Wand2, ShieldCheck, ShieldAlert, Boxes, ArrowUpRight } from "lucide-react";
import { CloudIcon, LangIcon } from "@/components/brand-icons";
import { ProjectSearch } from "@/components/project-search";
import { CiChecksPanel } from "@/components/ci-checks-panel";

function Metric({ label, value, hint, testid }) {
  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none" data-testid={testid}>
      <p className="font-mono-tight text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-3 font-display text-3xl font-semibold tracking-tight">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </Card>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    client.get("/compliance/summary").then((r) => setSummary(r.data));
  }, []);

  return (
    <div className="p-10">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">// overview</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight" data-testid="page-title">Compliance dashboard</h1>
          <p className="mt-1 text-sm text-slate-600">Live health of every IaC project scaffolded through Toastern.</p>
        </div>
        <Link to="/app/generate" data-testid="dashboard-generate">
          <Button className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800">
            <Wand2 className="mr-2 h-4 w-4" /> New project
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Metric label="Projects" value={summary?.total_projects ?? "—"} hint="Generated with Toastern" testid="metric-projects" />
        <Metric label="Compliant" value={summary?.compliant ?? "—"} hint={`${summary?.compliance_rate ?? 0}% of the workspace`} testid="metric-compliant" />
        <Metric label="Needs review" value={summary?.non_compliant ?? "—"} hint="Fail at least one policy" testid="metric-noncompliant" />
        <Metric label="Catalog" value={summary?.catalog_size ?? "—"} hint="Approved resources" testid="metric-catalog" />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="col-span-2 rounded-md border border-slate-200 bg-white p-6 shadow-none" data-testid="checks-panel">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <p className="font-mono-tight text-[11px] uppercase tracking-widest text-slate-500">// policies</p>
              <h2 className="mt-1 font-display text-lg font-semibold">Policy check distribution</h2>
            </div>
            <ShieldCheck className="h-4 w-4 text-slate-400" />
          </div>
          <div className="space-y-4">
            {summary && Object.entries(summary.by_check || {}).map(([label, v]) => {
              const total = v.pass + v.fail || 1;
              const pct = Math.round((v.pass / total) * 100);
              return (
                <div key={label}>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="text-slate-700">{label}</span>
                    <span className="font-mono-tight text-xs text-slate-500">{v.pass}/{v.pass + v.fail}</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full bg-slate-900 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {summary && !Object.keys(summary.by_check || {}).length && (
              <p className="text-sm text-slate-500">No projects yet. Scaffold one to populate this panel.</p>
            )}
          </div>
        </Card>

        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none" data-testid="cloud-split">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <p className="font-mono-tight text-[11px] uppercase tracking-widest text-slate-500">// clouds</p>
              <h2 className="mt-1 font-display text-lg font-semibold">By cloud</h2>
            </div>
            <Boxes className="h-4 w-4 text-slate-400" />
          </div>
          <div className="space-y-5">
            {summary && Object.entries(summary.by_cloud || {}).map(([cloud, v]) => (
              <div key={cloud} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CloudIcon cloud={cloud} className="h-4 w-4" />
                    <span className="text-sm font-medium uppercase tracking-widest text-slate-700">{cloud}</span>
                  </div>
                  <span className="font-mono-tight text-xs text-slate-500">{v.compliant}/{v.total}</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full transition-all"
                    style={{ width: `${(v.compliant / (v.total || 1)) * 100}%`, background: cloud === "azure" ? "#0078D4" : "#FF9900" }}
                  />
                </div>
              </div>
            ))}
            {summary && !Object.keys(summary.by_cloud || {}).length && (
              <p className="text-sm text-slate-500">Awaiting first project.</p>
            )}
          </div>
        </Card>
      </div>

      <div className="mt-8">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <p className="font-mono-tight text-[11px] uppercase tracking-widest text-slate-500">// recent</p>
            <h2 className="mt-1 font-display text-lg font-semibold">Recent projects</h2>
          </div>
          <div className="flex items-center gap-4">
            <ProjectSearch />
            <Link to="/app/projects" className="text-sm text-slate-600 hover:text-slate-900" data-testid="recent-view-all">
              View all <ArrowUpRight className="inline h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="recent-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-6 py-3 font-medium">Name</th>
                <th className="px-6 py-3 font-medium">Cloud</th>
                <th className="px-6 py-3 font-medium">IaC</th>
                <th className="px-6 py-3 font-medium">Region</th>
                <th className="px-6 py-3 font-medium">Score</th>
                <th className="px-6 py-3 text-right font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {summary?.recent_projects?.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`recent-row-${p.name}`}>
                  <td className="px-6 py-3 font-medium text-slate-900">
                    <Link to={`/app/projects/${p.id}`} className="hover:underline" data-testid={`recent-name-${p.name}`}>{p.name}</Link>
                  </td>
                  <td className="px-6 py-3">
                    <Badge variant="secondary" className="flex w-fit items-center gap-1.5 bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">
                      <CloudIcon cloud={p.cloud} className="h-3 w-3" />
                      {p.cloud}
                    </Badge>
                  </td>
                  <td className="px-6 py-3">
                    <span className="inline-flex items-center gap-1.5 font-mono-tight text-xs text-slate-600">
                      <LangIcon language={p.language} className="h-3 w-3" /> {p.language}
                    </span>
                  </td>
                  <td className="px-6 py-3 font-mono-tight text-xs text-slate-600">{p.region}</td>
                  <td className="px-6 py-3">
                    <span
                      className={
                        p.compliance?.compliant
                          ? "inline-flex items-center gap-1 text-emerald-600"
                          : "inline-flex items-center gap-1 text-amber-600"
                      }
                    >
                      {p.compliance?.compliant ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
                      {p.compliance?.score ?? "—"}%
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <Link to={`/app/projects/${p.id}`} className="text-slate-600 hover:text-slate-900" data-testid={`open-${p.name}`}>
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
              {(!summary || !summary.recent_projects?.length) && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-slate-500">No projects yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8">
        <CiChecksPanel />
      </div>
    </div>
  );
}
